import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStateSubject = new BehaviorSubject<boolean>(false);
  public authState$: Observable<boolean> = this.authStateSubject.asObservable();
  
  private userSubject = new BehaviorSubject<any>(null);
  public user$ = this.userSubject.asObservable();
  
  constructor(private router: Router) {
    // Initialize auth state from localStorage
    this.loadUserFromStorage();
  }

  /**
   * Load user data from localStorage and update the subjects
   */
  private loadUserFromStorage(): void {
    const user = this.getUserInfo();
    
    if (user) {
      this.userSubject.next(user);
      this.authStateSubject.next(true);
    }
  }

  /**
   * Set user information after successful login
   */
  setUserInfo(user: any, userRole: string = 'general'): void {
    localStorage.setItem('googleUser', JSON.stringify(user));
    localStorage.setItem('userRole', userRole);
    
    // Update subjects
    this.userSubject.next(user);
    this.authStateSubject.next(true);
    
    console.log('📊 User authenticated:', {
      email: user.email,
      name: user.name,
      role: userRole
    });
  }

  /**
   * Logout user - clear localStorage and update state
   */
  logout(): void {
    console.log('🚪 Logging out user...');
    
    // Clear all authentication data
    this.clearAuthData();
    
    // Update subjects
    this.userSubject.next(null);
    this.authStateSubject.next(false);
    
    // Navigate to home page
    this.router.navigate(['/']);
  }

  /**
   * Clear all authentication data from localStorage
   */
  private clearAuthData(): void {
    localStorage.removeItem('googleUser');
    localStorage.removeItem('userRole');
    localStorage.removeItem('sessionToken'); // Clean up old backend tokens
    localStorage.removeItem('userType'); // Legacy cleanup
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    const user = this.getUserInfo();
    return !!user;
  }

  /**
   * Get user information from localStorage
   */
  getUserInfo(): any {
    const googleUser = localStorage.getItem('googleUser');
    if (googleUser) {
      try {
        return JSON.parse(googleUser);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get user role from localStorage
   */
  getUserRole(): string {
    const role = localStorage.getItem('userRole');
    return role || 'general';
  }

  /**
   * Get user type (legacy support)
   */
  getUserType(): string {
    return this.getUserRole();
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const userRole = this.getUserRole();
    return userRole === role;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Check if user is internal
   */
  isInternal(): boolean {
    return this.hasRole('internal');
  }

  /**
   * Check if user is pilot
   */
  isPilot(): boolean {
    return this.hasRole('pilot');
  }

  /**
   * Get current user observable
   */
  getCurrentUser(): Observable<any> {
    return this.user$;
  }

  /**
   * Get current user synchronously
   */
  getCurrentUserSync(): any {
    return this.getUserInfo();
  }
}
